import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { DriverTaskData, AreaTaskData, DetailData } from '../types';
import { DriverFilterOption, buildDriverOptions, rememberDriverNames, toDriverIds } from '../../../../../utils/driverFilterOption';
import { useDailyTaskMonitoringQuery } from '../graphql/hooks/useDailyTaskMonitoringQuery';
import { useDailyTaskDetailQuery } from '../graphql/hooks/useDailyTaskDetailQuery';
import { useDailyTaskSummaryAreaQuery } from '../graphql/hooks/useDailyTaskSummaryAreaQuery';
import {
    SEARCH_DELIVERY_FILTER_AREA_OPTIONS,
    SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS,
    type DeliveryFilterOption,
    type SearchDeliveryFilterAreaOptionsResponse,
    type SearchDeliveryFilterDriverOptionsResponse
} from '../../parcelSlaMonitoring/graphql/queries/searchDeliveryFilterOptions';
import { useWarehouseContext } from "../../../../../contexts/WarehouseContext";
import { SiteType } from '../../../../../graphql/types/SiteType';
import { useHubWarehouseDspOptions } from '../../../../../hooks/useHubWarehouseDspOptions';
import { GET_ALL_EXCEPTION, ExceptionDictionaryResponse } from '../../../../../graphql/queries/getAllException';

export type ViewMode = 'driver' | 'area' | 'dsp' | 'whs';

export interface TaskMonitoringFilters {
    taskDate: dayjs.Dayjs;
    selectedWarehouses?: string[];
    selectedCities: string[];
    selectedDsps: string[];
    selectedAreas: string[];
    selectedDrivers: string[];
    viewMode: ViewMode;
}

// 工具函数：将名称转换为ID
const convertNamesToIds = (
    names: string[],
    items: Array<{ id: number; name: string }>
): number[] => {
    return names
        .map(name => {
            const item = items.find(item => item.name === name);
            return item ? parseInt(item.id.toString()) : null;
        })
        .filter(id => id !== null) as number[];
};

// 工具函数：获取状态字段映射
const getStatusField = (type: string, t: any): string | undefined => {
    const statusFieldMap: Record<string, string> = {
        'on_time_delivery': 'isOnTimeDelivery',
        'overdue_delivery': 'isOverdueDelivery',
        'transferred_in': 'isTransferredIn',
        'transferred_out': 'isTransferredOut',
        'inDelivery': 'isInDelivery',
        'allocatedParcels': 'isAllocatedParcels',
        'pickedUpFromWarehouse': 'isPickedUpFromWarehouse',
        'warehouseLost': 'isWarehouseLost',
        'returned': 'isReturned',
        'damaged': 'isDamaged',
        'unclaimed': 'isUnclaimed'
    };
    return statusFieldMap[type];
};

// 工具函数：检查是否为状态类型
const isStatusType = (type: string): boolean => {
    const statusTypes = ['on_time_delivery', 'overdue_delivery', 'transferred_in', 'transferred_out', 'inDelivery'];
    return statusTypes.includes(type);
};

export interface UseTaskMonitoringResult {
    filters: TaskMonitoringFilters;
    setFilters: (filters: TaskMonitoringFilters) => void;
    driverData: DriverTaskData[];
    areaData: AreaTaskData[];
    summaryData: any;
    cities: string[];
    warehouses: Array<{ id: string; name: string }>;
    dsps: Array<{ id: number; name: string }>;
    areas: Array<{ id: number; name: string }>;
    drivers: DriverFilterOption[];
    /** 详情抽屉用：DSP-司机级联后的司机下拉选项（选 DSP 时过滤；未选 DSP 全量） */
    drawerDrivers: DriverFilterOption[];
    /** 区域下拉的远程搜索入口（选项已改为按关键词查主库，不再聚合 AI 库汇总表）。
     *  Hub 页顶部筛选只有 WHS/DSP，区域与司机下拉仅存在于详情抽屉，故关键词由抽屉驱动。 */
    onAreaSearch: (keyword: string) => void;
    areaOptionsLoading: boolean;
    /** 详情抽屉内司机远程搜索（带 DSP 级联限定） */
    onDrawerDriverSearch: (keyword: string) => void;
    drawerDriverOptionsLoading: boolean;
    drawerVisible: boolean;
    drawerTitle: string;
    drawerData: DetailData[];
    drawerColumns: any[];
    setDrawerVisible: (visible: boolean) => void;
    handleDetailClick: (type: string, record: DriverTaskData | AreaTaskData) => void;
    handleUndeliveredDistributionClick: (type: 'undelivered' | 'unclaimed' | 'inDelivery' | 'returned' | 'damaged' | 'warehouseLost') => void;
    handleDeliveryProgressClick: (type: 'allocatedParcels' | 'pickedUpFromWarehouse' | 'delivered') => void;
    statusTypeOptions: Array<{ value: string; label: string }>;
    handleDetailStatusTypeChange: (statusTypes: string[]) => void;
    dataUpdatedAtText: string;
    handlePageChange: (page: number, pageSize?: number) => void;
    handleDetailPageChange: (page: number, pageSize?: number) => void;
    handleSortChange: (sortBy: string, sortDirection: 'ASC' | 'DESC') => void;
    handleDetailSortChange: (sortBy: string, sortDirection: 'ASC' | 'DESC') => void;
    sortConfig: {
        sortBy: string;
        sortDirection: 'ASC' | 'DESC';
    };
    detailSortConfig: {
        sortBy: string;
        sortDirection: 'ASC' | 'DESC';
    };
    detailPagination: any;
    detailLoading: boolean;
    detailFilters: {
        selectedWarehouses?: string[];
        selectedDsps: string[];
        selectedAreas: string[];
        selectedDrivers: string[];
    };
    setDetailFilters: React.Dispatch<React.SetStateAction<{
        selectedWarehouses?: string[];
        selectedDsps: string[];
        selectedAreas: string[];
        selectedDrivers: string[];
    }>>;
    detailStatusTypes: string[];
    loading: boolean;
    pagination: any;
    tablePagination: any;
    orderDetailVisible: boolean;
    orderId: string;
    setOrderDetailVisible: (visible: boolean) => void;
    showOrderDetail: (orderId: number) => void;
    warehouseId: string;
    lastDetailContext: { type: string; record: DriverTaskData | AreaTaskData; filters: TaskMonitoringFilters; statusField?: string; statusFields?: string[] } | null;
    getCurrentSummaryDownloadArgs: () => {
        taskDate: string;
        viewMode: string;
        hubId?: number;
        warehouseIds?: number[];
        dspIds?: number[];
        cityNames?: string[];
        sortBy?: string;
        sortDirection?: 'ASC' | 'DESC';
    } | null;
}

// 维度类型常量
const DIMENSION_TYPES = {
    DSP: 'dsp',
    WHS: 'whs',
    AREA: 'area',
    DRIVER: 'driver'
} as const;

export const useHubTaskMonitoring = (): UseTaskMonitoringResult => {
    const { t, i18n } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const warehouseId = currentWarehouse?.id!;
    const { data: exceptionData } = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION, { fetchPolicy: 'cache-first' });
    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters ?? [];
    // 与包裹详情历史一致：只展示当前语言的 secondaryType 文案，不拼接 code
    const getLastFailureReasonDisplayText = useCallback((code: string): string => {
        if (!code) return '';
        const ex = exceptionList.find((e: { exceptionReasonCode: string }) => e.exceptionReasonCode === code);
        if (!ex) return '';
        const lang = i18n.language?.startsWith('zh') ? 'zh' : i18n.language?.startsWith('es') ? 'es' : 'en';
        if (lang === 'zh' && ex.secondaryTypeZh) return ex.secondaryTypeZh;
        if (lang === 'es' && ex.secondaryTypeEs) return ex.secondaryTypeEs;
        if (ex.secondaryTypeEn) return ex.secondaryTypeEn;
        return ex.exceptionReasonCode;
    }, [exceptionList, i18n.language]);

    const [filters, setFilters] = useState<TaskMonitoringFilters>({
        taskDate: dayjs(), // 默认时间为今天
        selectedWarehouses: [],
        selectedCities: [],
        selectedDsps: [],
        selectedAreas: [],
        selectedDrivers: [],
        viewMode: 'whs' // Hub 页面默认使用 'whs'
    });
    
    // 分页状态
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10); // 一页10条
    

    // 排序状态：默认按应派件量从高到低
    const [sortConfig, setSortConfig] = useState<{
        sortBy: string;
        sortDirection: 'ASC' | 'DESC';
    }>({
        sortBy: 'allocatedParcels',
        sortDirection: 'DESC'
    });

    // 详情排序状态 - 默认按状态排序（降序，DELIVERY_FAILED排在前面）
    const [detailSortConfig, setDetailSortConfig] = useState<{
        sortBy: string;
        sortDirection: 'ASC' | 'DESC';
    }>({
        sortBy: 'overallStatus',
        sortDirection: 'DESC'
    });
    
    // 抽屉相关状态
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerTitle, setDrawerTitle] = useState('');
    const [drawerData, setDrawerData] = useState<DetailData[]>([]);
    const [drawerColumns, setDrawerColumns] = useState<any[]>([]);
    const [dataUpdatedAtText, setDataUpdatedAtText] = useState<string>('');
    const [detailStatusTypes, setDetailStatusTypes] = useState<string[]>([]);
    
    // 订单详情相关状态
    const [orderDetailVisible, setOrderDetailVisible] = useState(false);
    const [orderId, setOrderId] = useState<string>('');

    // Hub 页面查询参数：传递树状节点的 hubId
    const getAreaQueryParams = () => ({
        hubId: currentWarehouse?.id ? parseInt(currentWarehouse.id) : undefined,
    });

    // Hub 页面只有 DSP 维度和 WHS 维度
    const isDspDimension = (): boolean => filters.viewMode === 'dsp';
    const isWhsDimension = (): boolean => filters.viewMode === 'whs';

    // 计算当前维度类型
    // viewMode 映射规则：
    // - Hub 页面：'dsp' = DSP 维度，'whs' = WHS 维度
    const getCurrentDimensionType = (): string => {
        // Hub 页面：根据 viewMode 返回对应的维度类型
        if (filters.viewMode === 'dsp') {
            return DIMENSION_TYPES.DSP;
        }
        if (filters.viewMode === 'whs') {
            return DIMENSION_TYPES.WHS;
        }
        // 如果传入其他值，抛出错误
        throw Error("Hub 页面只支持 'dsp' 和 'whs' 维度")
    };

    // 使用GraphQL查询 - 区域维度
    const areaQueryParams = getAreaQueryParams();
    const {
        summaryData: areaSummaryData,
        recordData: areaRecordData,
        calculatedAt: areaCalculatedAt,
        loading: areaLoading,
        handleFilter: handleAreaFilter,
        clearData: clearAreaData,
        pagination: areaApiPagination
    } = useDailyTaskSummaryAreaQuery({
        ...areaQueryParams,
        dimensionType: getCurrentDimensionType(),
        mode: 'hub'
    });

    // 使用GraphQL查询 - 司机维度
    const { 
        summaryData: driverSummaryData, 
        recordData: driverRecordData, 
        calculatedAt: driverCalculatedAt,
        loading: driverLoading,
        handleFilter: handleDriverFilter,
        clearData: clearDriverData,
        pagination: driverApiPagination
    } = useDailyTaskMonitoringQuery('whs');

    // 详情查询相关状态
    const [currentDetailType, setCurrentDetailType] = useState<string>('');
    const [currentDetailRecord, setCurrentDetailRecord] = useState<DriverTaskData | AreaTaskData | null>(null);
    const [lastDetailContext, setLastDetailContext] = useState<{ type: string; record: DriverTaskData | AreaTaskData; filters: TaskMonitoringFilters; statusField?: string; statusFields?: string[] } | null>(null);
    
    const skipDetailFiltersEffectRef = React.useRef(false);
    
    // 详情页筛选条件
    const [detailFilters, setDetailFilters] = useState<{
        selectedWarehouses?: string[];
        selectedDsps: string[];
        selectedAreas: string[];
        selectedDrivers: string[];
    }>({
        selectedWarehouses: [],
        selectedDsps: [],
        selectedAreas: [],
        selectedDrivers: []
    });

    const { 
        detailData: newDetailData,
        detailDataRequestId,
        pagination: detailPagination, 
        loading: detailLoading,
        fetchDetailsByStatusField,
        fetchDetailsByStatusFieldForDriver
    } = useDailyTaskDetailQuery('whs');

    // 用于忽略过期响应，避免 Hub 详情抽屉在快速切换状态时出现条数不一致
    const detailRequestIdRef = React.useRef(0);

    // 使用 useMemo 稳定 taskDate 字符串，避免每次渲染都生成新字符串导致重复触发
    const taskDateString = useMemo(() => filters.taskDate.format('YYYY-MM-DD'), [filters.taskDate]);

    // 区域/司机选项改为按关键词查主库（原先由 FilterOptions 聚合 AI 库汇总表，约 9.6s 且下拉常为空）。
    // 下游多处用「名称」反查 id 提交筛选，而搜索结果随关键词变化，
    // 故累积缓存所有见过的 名称→id，替代原先「按维度 + taskDate 缓存整份选项」的做法。
    const areaIdByName = useRef<Map<string, number>>(new Map());
    // 司机下拉的 value 是 accountId，反向缓存用于已选项回显与抽屉标题显示姓名
    const driverNameById = useRef<Map<number, string>>(new Map());
    const [areaKeyword, setAreaKeyword] = useState('');

    // 获取当前hub ID（用于hub页面的过滤）
    // 筛选选项查询的目的是获取所有可用的选项，而不是根据已选择的选项来过滤
    // 获取当前hub ID（用于hub页面的过滤）
    // 后端 getHubTaskMonitoringFilterOptions 已接入 @RequireResourceAndWarehouse，需要显式传 hubId 做仓库权限校验。
    // Hub Manager 的 currentWarehouse 就是其所属 hub（siteType=HUB），与 HQ 场景取值一致，统一走 currentWarehouse.id。
    const getCurrentHubId = () => {
        if (currentWarehouse?.siteType === SiteType.HUB) {
            return currentWarehouse.id;
        }
        return undefined;
    };

    const hubIdForFilterOptions = getCurrentHubId();

    // Hub 结构关联的 WHS/DSP 下拉选项：按 hubId scope，WHS 选择变化时级联重查 DSP（替代旧权限接口）
    const hubIdNum = hubIdForFilterOptions != null ? Number(hubIdForFilterOptions) : undefined;
    const { warehouses: structuralWarehouses, dsps: structuralDsps } = useHubWarehouseDspOptions(hubIdNum, filters.selectedWarehouses);

    // 区域/司机搜索范围：优先用已选 WHS，否则由后端按 hubId 展开为该 hub 下的全部仓库
    const optionScopeWarehouseIds = useMemo(() => {
        const selected = (filters.selectedWarehouses ?? [])
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id));
        return selected.length > 0 ? selected : undefined;
    }, [filters.selectedWarehouses]);

    const { data: areaOptionsData, loading: areaOptionsLoading } = useQuery<SearchDeliveryFilterAreaOptionsResponse>(
        SEARCH_DELIVERY_FILTER_AREA_OPTIONS,
        {
            variables: { hubId: hubIdNum, warehouseIds: optionScopeWarehouseIds, keyword: areaKeyword || null },
            skip: !hubIdNum
        }
    );

    // Hub 页司机下拉只存在于详情抽屉，且抽屉有自己的 DSP 级联搜索查询。
    // 此处不带关键词，仅用于提供「范围内前 N 个司机」作为抽屉查询未返回时的兜底列表。
    const { data: driverOptionsData } = useQuery<SearchDeliveryFilterDriverOptionsResponse>(
        SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS,
        {
            variables: { hubId: hubIdNum, warehouseIds: optionScopeWarehouseIds, keyword: null },
            skip: !hubIdNum
        }
    );

    const searchedAreas = areaOptionsData?.searchDeliveryFilterAreaOptions;
    const searchedDrivers = driverOptionsData?.searchDeliveryFilterDriverOptions;

    searchedAreas?.forEach((option) => areaIdByName.current.set(option.name, option.id));
    rememberDriverNames(searchedDrivers, driverNameById.current);

    /** 行下钻会把行上的区域/司机写进抽屉筛选，这些行通常不在搜索结果里，故先把行上的名称与 id 记入缓存 */
    const rememberRecordOptionIds = (record: DriverTaskData | AreaTaskData) => {
        if ('area' in record && record.area && record.areaId) areaIdByName.current.set(record.area, Number(record.areaId));
        if ('driverName' in record && record.driverName && record.driverId) {
            driverNameById.current.set(Number(record.driverId), record.driverName);
        }
    };

    /** 搜索结果并入「已选项」，否则 antd Select 无法回显已选标签 */
    const mergeSelectedIntoOptions = (
        searched: DeliveryFilterOption[] | undefined,
        selectedNames: string[] | undefined,
        idByName: Map<string, number>
    ): DeliveryFilterOption[] => {
        const merged = [...(searched ?? [])];
        (selectedNames ?? []).forEach((name) => {
            if (!name || merged.some((option) => option.name === name)) return;
            merged.push({ id: idByName.get(name) ?? -1, name });
        });
        return merged;
    };

    // 城市下拉在 HubTaskMonitoringFilter / TaskDetailFilter 中均未渲染，原 FilterOptions 的 cities 属于死字段
    const cities: string[] = [];
    // DSP 下拉改由结构关联查询提供（与日期无关，随 WHS 选择级联），替代原按当天汇总数据去重
    const dsps = structuralDsps;
    // 区域下拉只展示唯一的区域名称，避免同名区域在不同 WHS 下重复出现
    const areas = useMemo(
        () => {
            const merged = mergeSelectedIntoOptions(searchedAreas, detailFilters.selectedAreas, areaIdByName.current);
            const seen = new Set<string>();
            return merged.filter((area) => {
                if (!area.name || seen.has(area.name)) return false;
                seen.add(area.name);
                return true;
            });
        },
        [searchedAreas, detailFilters.selectedAreas]
    );
    const drivers = useMemo(
        () => buildDriverOptions(searchedDrivers, detailFilters.selectedDrivers, driverNameById.current),
        [searchedDrivers, detailFilters.selectedDrivers]
    );

    // 详情抽屉内 DSP-司机级联：
    // - `drivers` 永远是全量司机（不随抽屉内筛选变化）
    // - `drawerDrivers` 会在抽屉内选中 DSP 时，只显示该 DSP 下的司机
    const drawerCascadedDspIds = useMemo(() => {
        if (!detailFilters.selectedDsps || detailFilters.selectedDsps.length === 0) return null;
        const ids = detailFilters.selectedDsps
            .map((name) => dsps.find((d: { id: number; name: string }) => d.name === name)?.id)
            .filter((id): id is number => id != null);
        return ids.length > 0 ? ids : null;
    }, [detailFilters.selectedDsps, dsps]);

    // 抽屉内 DSP→司机级联同样走主库按需搜索，与主列表口径一致
    const [drawerDriverKeyword, setDrawerDriverKeyword] = useState('');
    const { data: drawerCascadedDriverData, loading: drawerDriverOptionsLoading } =
        useQuery<SearchDeliveryFilterDriverOptionsResponse>(SEARCH_DELIVERY_FILTER_DRIVER_OPTIONS, {
            variables: {
                hubId: hubIdNum,
                warehouseIds: optionScopeWarehouseIds,
                dspIds: drawerCascadedDspIds ?? undefined,
                keyword: drawerDriverKeyword || null
            },
            skip: !hubIdNum || !drawerVisible
        });

    const drawerDrivers = useMemo(() => {
        const searched = drawerCascadedDriverData?.searchDeliveryFilterDriverOptions;
        rememberDriverNames(searched, driverNameById.current);
        if (!searched) return drivers;
        return buildDriverOptions(searched, detailFilters.selectedDrivers, driverNameById.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerCascadedDriverData, drivers, detailFilters.selectedDrivers]);

    // 约束：司机选择必须来自当前下拉选项，否则筛选条件与下拉展示不一致。
    // 因此当 DSP 改变后，如果“已选司机”不在 `drawerDrivers` 中，自动清理无效项。
    useEffect(() => {
        if (!drawerVisible || !drawerCascadedDspIds) return;
        if (!detailFilters.selectedDrivers || detailFilters.selectedDrivers.length === 0) return;
        const allowed = new Set(drawerDrivers.map((d) => String(d.accountId)));
        const nextSelectedDrivers = detailFilters.selectedDrivers.filter((value) => allowed.has(value));
        if (nextSelectedDrivers.length === detailFilters.selectedDrivers.length) return;
        setDetailFilters((prev) => ({
            ...prev,
            selectedDrivers: nextSelectedDrivers,
        }));
    }, [drawerVisible, drawerCascadedDspIds, drawerDrivers, detailFilters.selectedDrivers, setDetailFilters]);

    // 处理司机维度数据（仅在司机维度模式下处理）
    const driverData: DriverTaskData[] = useMemo(() => {
        // 如果当前不是司机维度模式，返回空数组，避免不必要的处理
        if (filters.viewMode !== 'driver' || driverRecordData.length === 0) {
            return [];
        }
        return driverRecordData
            .filter(record => record.driverId && record.driverId.toString() !== '0' && record.driverId.toString() !== '999')
            .map(record => {
                const onTimeDelivery = Number((record as any).onTimeDelivery) || 0;
                const overdueDelivery = Number((record as any).overdueDelivery) || 0;
                const deliveredCount = (record as any).deliveredCount != null 
                    ? Number((record as any).deliveredCount) 
                    : 0;
                const pickedUpFromWarehouse = Number((record as any).pickedUpFromWarehouse) || 0;
                const transferredIn = Number((record as any).transferredIn) || 0;
                const allocatedParcels = Number((record as any).allocatedParcels) || 0;
                const scheduledParcels = allocatedParcels + transferredIn;
                const deliveryRate = scheduledParcels > 0 
                    ? Number((deliveredCount / scheduledParcels * 100).toFixed(2))
                    : 0;
                const returned = Number((record as any).returned) || 0;
                const damaged = Number((record as any).damaged) || 0;
                const failedDeliveryRate = pickedUpFromWarehouse > 0
                    ? Number(((returned + damaged) / pickedUpFromWarehouse * 100).toFixed(2))
                    : 0;
                
                let dspName = (record as any).dspName;
                let dspId = (record as any).dspId;
                if (!dspName && dspId) {
                    const dspFromOptions = dsps.find((d: { id: number; name: string }) => d.id === dspId);
                    dspName = dspFromOptions?.name || `DSP-${dspId}`;
                } else if (!dspName) {
                    dspName = '-';
                }
                // 如果 dspId 不存在，尝试从 dsp 名称查找
                if (!dspId || dspId <= 0) {
                    if (dspName && dspName !== '-') {
                        const dspFromOptions = dsps.find((d: { id: number; name: string }) => d.name === dspName);
                        dspId = dspFromOptions?.id;
                    }
                }
                const driverName = (record as any).driverName || `Driver-${record.driverId}`;
                const warehouseName = (record as any).warehouseName || currentWarehouse?.name || undefined;
                
                return {
                    id: Number(record.id) || 0,
                    driverId: record.driverId?.toString() || '',
                    driverName: driverName,
                    warehouseName,
                    dsp: dspName,
                    dspId: dspId, // 确保包含 dspId，用于下钻时传递
                    allocatedParcels: Number((record as any).allocatedParcels) || 0,
                    pickedUpFromWarehouse,
                    warehouseLost: Number((record as any).warehouseLost) || 0,
                    inDelivery: Number((record as any).inDelivery) || 0,
                    onTimeDelivery,
                    overdueDelivery,
                    deliveredCount,
                    unclaimedCount: Number((record as any).unclaimedCount) || 0,
                    noAscanDelivery: Number((record as any).noAscanDelivery) || 0,
                    transferredIn: Number((record as any).transferredIn) || 0,
                    transferredOut: Number((record as any).transferredOut) || 0,
                    returned,
                    damaged,
                    lastDeliveryTime: (record as any).lastDeliveryTime || undefined,
                    dwellTime: (record as any).dwellTime || undefined,
                    deliveryRate,
                    failedDeliveryRate
                };
            });
    }, [driverRecordData, dsps, filters.viewMode, currentWarehouse?.name]);

    // 司机维度数据处理完成

    // 直接使用区域汇总数据，不进行聚合
    const areaData: AreaTaskData[] = areaRecordData.map((record, index) => {
        // 优先使用后端返回的 dspName，如果为空则从筛选选项中查找
        let dspName = record.dspName;
        if (!dspName && record.dspId) {
            const dspFromOptions = dsps.find((d: { id: number; name: string }) => d.id === record.dspId);
            dspName = dspFromOptions?.name || `DSP-${record.dspId}`;
        } else if (!dspName) {
            dspName = '-';
        }
        
        return {
            id: record.id || `area-${record.areaId}-${record.dspId}-${index}`,
            area: record.areaName || `Area-${record.areaId}`,
            dsp: dspName || '-',
            warehouseName: (record as any).warehouseName || currentWarehouse?.name || undefined,
            warehouseId: Number((record as any).warehouseId) || undefined,
            areaId: record.areaId,
            dspId: record.dspId,
            allocatedParcels: Number(record.allocatedParcels) || 0,
            pickedUpFromWarehouse: Number(record.pickedUpFromWarehouse) || 0,
            warehouseLost: Number(record.warehouseLost) || 0,
            inDelivery: Number(record.inDelivery) || 0,
            onTimeDelivery: Number(record.onTimeDelivery) || 0,
            overdueDelivery: Number(record.overdueDelivery) || 0,
            deliveredCount: Number((record as any).deliveredCount) || 0,
            unclaimedCount: Number((record as any).unclaimedCount) || 0,
            noAscanDelivery: Number((record as any).noAscanDelivery) || 0,
            transferredIn: Number(record.transferredIn) || 0,
            transferredOut: Number(record.transferredOut) || 0,
            returned: Number(record.returned) || 0,
            damaged: Number(record.damaged) || 0,
            deliveryRate: (() => {
                const deliveredCount = Number((record as any).deliveredCount) || 0;
                const transferredIn = Number(record.transferredIn) || 0;
                const allocatedParcels = Number(record.allocatedParcels) || 0;
                const scheduledParcels = allocatedParcels + transferredIn;

                if (scheduledParcels === 0) return 0;
                return Number((deliveredCount / scheduledParcels * 100).toFixed(2));
            })(),
            failedDeliveryRate: (() => {
                const returned = Number(record.returned) || 0;
                const damaged = Number(record.damaged) || 0;
                const pickedUpFromWarehouse = Number(record.pickedUpFromWarehouse) || 0;
            
                if (pickedUpFromWarehouse === 0) return 0;
                return Number(((returned + damaged) / pickedUpFromWarehouse * 100).toFixed(2));
            })()
        };
    });

    // Hub 页面使用 areaSummaryData，Daily 页面根据 viewMode 选择
    const currentSummaryData = areaSummaryData;

    // 数据更新时间（用于页面展示：数据更新于 XXX）——使用后端返回的 calculatedAt
    useEffect(() => {
        const calculatedAt = areaCalculatedAt;
        if (!calculatedAt) {
            setDataUpdatedAtText('');
            return;
        }
        try {
            const d = dayjs(calculatedAt);
            setDataUpdatedAtText(d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : String(calculatedAt));
        } catch {
            setDataUpdatedAtText(String(calculatedAt));
        }
    }, [filters.viewMode, driverCalculatedAt, areaCalculatedAt]);

    // 状态文本转换
    const getStatusText = (status: number): string => {
        const statusMap: { [key: number]: string } = {
            1: t('wms.delivery.dailyTaskMonitoring.status.inProgress'),
            2: t('wms.delivery.dailyTaskMonitoring.status.delivered'),
            3: t('wms.delivery.dailyTaskMonitoring.status.returned'),
            4: t('wms.delivery.dailyTaskMonitoring.status.damaged'),
            5: t('wms.delivery.dailyTaskMonitoring.status.lost')
        };
        return statusMap[status] || t('wms.delivery.dailyTaskMonitoring.status.unknown');
    };

    // 显示订单详情
    const showOrderDetail = (orderId: number) => {
        if (orderId) {
            setOrderId(orderId.toString());
            setOrderDetailVisible(true);
        }
    };

    // 根据类型获取列定义
    const getColumnsByType = (type: string, detailType?: string) => {
        const isDriverDimension = (detailType || currentDetailType) === 'driver';
        const driverColumnTitle = isDriverDimension 
            ? t('wms.delivery.dailyTaskMonitoring.detailColumns.deliveryDriver')
            : t('wms.delivery.dailyTaskMonitoring.detailColumns.driver');
        
        const whsNameColumn = {
            title: t('wms.delivery.dailyTaskMonitoring.detailColumns.whsName'),
            dataIndex: 'warehouse_name',
            key: 'warehouse_name',
            render: (text: string) => (text && text.trim().length > 0 ? text : '-')
        };
        const baseColumns = [
            whsNameColumn,
            {
                title: t('wms.delivery.dailyTaskMonitoring.detailColumns.trackingNumber'),
                dataIndex: 'tracking_number',
                key: 'tracking_number',
                render: (text: string, record: any) => React.createElement('a', {
                    href: '#',
                    onClick: (e: React.MouseEvent) => {
                            e.preventDefault();
                            showOrderDetail(record.order_id);
                    },
                    style: { color: '#1890ff' }
                }, text)
            },
            { title: t('wms.delivery.dailyTaskMonitoring.detailColumns.dsp'), dataIndex: 'dsp_name', key: 'dsp_name', sortable: true, render: (text: string) => (text && text.trim().length > 0 ? text : '-') },
            { title: t('wms.delivery.dailyTaskMonitoring.detailColumns.area'), dataIndex: 'area_name', key: 'area_name', sortable: true, render: (text: string) => (text && text.trim().length > 0 ? text : '-') },
            { title: driverColumnTitle, dataIndex: 'driver_name', key: 'driver_name', sortable: true, render: (text: string) => (text && text.trim().length > 0 ? text : '-') },
            { title: t('wms.delivery.dailyTaskMonitoring.detailColumns.taskDate'), dataIndex: 'task_date', key: 'task_date', render: (text: string) => text || '-' },
            { title: t('wms.delivery.dailyTaskMonitoring.detailColumns.status'), dataIndex: 'status', key: 'status', sortable: true, render: (text: string) => text || '-' },
            { title: t('wms.delivery.dailyTaskMonitoring.detailColumns.lastFailureReason'), dataIndex: 'last_failure_reason', key: 'last_failure_reason', render: (text: string, record: DetailData) => { const code = record.exception_reason_code; const displayText = code ? getLastFailureReasonDisplayText(code) : ''; return (displayText && displayText.trim().length > 0) ? displayText : (text && text.trim().length > 0 ? text : '-'); } },
            {
                title: t('wms.delivery.dailyTaskMonitoring.detailColumns.latestPhysicalTrackingUpdatedAt'),
                dataIndex: 'latest_physical_tracking_updated_at',
                key: 'latest_physical_tracking_updated_at',
                render: (text: string) => {
                    if (!text) return '-';
                    const formatted = dayjs(text).isValid() ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : text;
                    return formatted;
                }
            }
        ];

        if (type === 'delivered') {
            return [...baseColumns, { title: t('wms.delivery.dailyTaskMonitoring.detailColumns.deliveredTime'), dataIndex: 'delivered_time', key: 'delivered_time', sortable: true }];
        }

        return baseColumns;
    };

    // 点击数字查看详情
    const handleDetailClick = async (type: string, record: DriverTaskData | AreaTaskData) => {
        rememberRecordOptionIds(record);

        const typeMap: { [key: string]: string } = {
            'total_parcels': t('wms.delivery.dailyTaskMonitoring.areaColumns.totalScheduledParcels'),
            // 仅“已妥投”（避免显示为“已妥投/实际领取”）
            'delivered': t('wms.delivery.dailyTaskMonitoring.tableColumns.delivered'),
            'in_progress': t('wms.delivery.dailyTaskMonitoring.areaColumns.delivering'),
            'inDelivery': t('wms.delivery.dailyTaskMonitoring.areaColumns.delivering'),
            'returned': t('wms.delivery.dailyTaskMonitoring.areaColumns.failedReturned'),
            // 与表格/未妥投分布保持一致：派送失败未回仓
            'damaged': t('wms.delivery.dailyTaskMonitoring.tableColumns.failedNotReturned'),
            'lost': t('wms.delivery.dailyTaskMonitoring.tableColumns.failedNotReturned'),
            'on_time_delivery': t('wms.delivery.dailyTaskMonitoring.areaColumns.onTimeDelivery'),
            'overdue_delivery': t('wms.delivery.dailyTaskMonitoring.areaColumns.delayedDelivery'),
            'transferred_in': t('wms.delivery.dailyTaskMonitoring.areaColumns.transferInToday'),
            'transferred_out': t('wms.delivery.dailyTaskMonitoring.areaColumns.transferOutToday'),
            // 与表格列标题保持一致：应派件量
            'allocatedParcels': t('wms.delivery.dailyTaskMonitoring.tableColumns.scheduledParcels'),
            'pickedUpFromWarehouse': t('wms.delivery.dailyTaskMonitoring.summary.actualPickedUp'),
            // 与表格列标题保持一致：仓内疑似丢失
            'warehouseLost': t('wms.delivery.dailyTaskMonitoring.tableColumns.warehouseLost'),
            'unclaimed': t('wms.delivery.dailyTaskMonitoring.areaColumns.unclaimed'),
            'no_ascan_delivery': t('wms.delivery.dailyTaskMonitoring.areaColumns.noAscanDelivery')
        };

        // 配送效率状态映射
        const deliveryEfficiencyStatusMap: { [key: string]: string } = {
            'on_time_delivery': 'isOnTimeDelivery',    // 准时妥投
            'overdue_delivery': 'isOverdueDelivery',  // 超时妥投
            'transferred_in': 'isTransferredIn',      // 当日转入
            'transferred_out': 'isTransferredOut',    // 当日转出
            'inDelivery': 'isInDelivery',             // 派送中
            'damaged': 'isDamaged',                   // 失败未回仓
            'allocatedParcels': 'isAllocatedParcels',  // 线路应派总量
            'pickedUpFromWarehouse': 'isPickedUpFromWarehouse',  // 司机实领
            'warehouseLost': 'isWarehouseLost',      // 仓内丢失
            'returned': 'isReturned',                 // 失败已回仓
            'unclaimed': 'isUnclaimed',              // 未领取
            'delivered': 'isDelivered',               // 已妥投（包含准时/超时/无Ascan）
            'no_ascan_delivery': 'noAscanDelivery'    // 无Ascan妥投
        };
        
        

        // 获取对应类型的标签名称
        const typeLabel = typeMap[type] || type;
        
        const titlePrefix = (() => {
            // Hub 页 WHS/DSP 视角：按 viewMode 决定标题显示内容
            if (isWhsDimension()) {
                // WHS 视角：只显示仓库名称
                return (record as any).warehouseName || currentWarehouse?.name || '';
            }
            // DSP 视角：只显示 DSP 名称
            return (record as any).dsp || (record as any).dspName || '';
        })();
        setDrawerTitle(`${titlePrefix} - ${typeLabel} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')}`);
        
        // 保存当前详情类型和记录
        const detailType = 'driverName' in record ? 'driver' : 'area';
        setCurrentDetailType(detailType);
        setCurrentDetailRecord(record);
        
        // 传递 detailType 给 getColumnsByType，避免依赖异步状态更新
        const columns = getColumnsByType(type, detailType);
        setDrawerColumns(columns);
        
        
        let contextFilters = { ...filters };
        if (filters.selectedDsps.length === 0 && 'dsp' in record && record.dsp) {
            contextFilters.selectedDsps = [record.dsp];
        }
        if (filters.selectedAreas.length === 0 && 'area' in record && record.area) {
            contextFilters.selectedAreas = [record.area];
        }
        
        const statusFieldForContext: string | undefined = deliveryEfficiencyStatusMap[type];
        
        // 保存原始详情上下文，包含当前的筛选条件和 statusField
        setLastDetailContext({ type, record, filters: contextFilters, statusField: statusFieldForContext });
        // 初始化抽屉状态筛选：默认带过去当前下钻列（仅当属于状态列表）
        setDetailStatusTypes(statusTypeOptions.some(opt => opt.value === type) ? [type] : []);
        
        // 初始化详情筛选条件：Hub WHS/DSP 视角按维度区分
        const initialDetailFilters = (() => {
            if (isWhsDimension()) {
                // Hub WHS 视角：带 WHS 筛选（只按 warehouseId 和状态过滤）
                const warehouseNameForFilter = (record as any).warehouseName || currentWarehouse?.name || '';
                return {
                    selectedWarehouses: warehouseNameForFilter ? [warehouseNameForFilter] : [],
                    selectedDsps: [],
                    selectedAreas: [],
                    selectedDrivers: []
                };
            }
            // Hub DSP 视角：默认预填当前行的 DSP（与下钻查询逻辑一致）
            const dspForFilter = (record as any).dsp || (record as any).dspName || '';
            return {
                selectedWarehouses: [],
                selectedDsps: dspForFilter ? [dspForFilter] : [],
                selectedAreas: [],
                selectedDrivers: []
            };
        })();
        // 标记跳过 useEffect 的自动查询，避免重复请求
        skipDetailFiltersEffectRef.current = true;
        setDetailFilters(initialDetailFilters);
        
        // 先显示抽屉，避免第二次点击不显示的问题
        setDrawerVisible(true);
        
        // 根据记录类型调用相应的查询 - 默认按状态排序
        const pageRequest = {
            page: 1,
            size: 10,
            sortBy: detailSortConfig.sortBy,
            sortDirection: detailSortConfig.sortDirection
        };
        
        try {
            if ('driverName' in record) {
                // 司机维度 - 传递当前行的 dspId（司机维度不可能是hub视角，不需要判断）
                // 司机 ID 只能取行数据：drivers 选项是按关键词搜索的有限结果，表格行内司机通常不在其中
                const currentDriverId = 'driverId' in record && record.driverId ? parseInt(record.driverId.toString()) : undefined;
                if (currentDriverId) {
                    // 获取当前行的 dspId（司机维度表格中数值下钻必须传当前行的 dspId）
                    // 优先使用 record.dspId，如果没有则从 dsps 列表中根据 dsp 名称查找
                    let rowDspId = (record as DriverTaskData).dspId;
                    if (!rowDspId || rowDspId <= 0) {
                        // 如果 dspId 不存在，尝试从 dsp 名称查找
                        const dspName = (record as DriverTaskData).dsp;
                        if (dspName) {
                            const dspFromOptions = dsps.find((d: { id: number; name: string }) => d.name === dspName);
                            rowDspId = dspFromOptions?.id;
                        }
                    }
                    const dspIdsForQuery = rowDspId && rowDspId > 0 ? [rowDspId] : undefined;

                    // 获取状态字段
                    const statusField: string | undefined = deliveryEfficiencyStatusMap[type];
                    
                    // Daily 页面司机维度：传递当前行的 dspId + driverId + 树状节点的 warehouseId
                    await fetchDetailsByStatusFieldForDriver(
                        filters.taskDate.format('YYYY-MM-DD'), 
                        statusField ? [statusField] : undefined,
                        pageRequest,
                        warehouseId, // Daily 页面司机维度：传递树状节点的 warehouseId
                        dspIdsForQuery, // 当前行的 dspId（必须传）
                        undefined, // areaIds - 司机维度详情点击时不使用区域筛选
                        [currentDriverId], // 当前行的司机ID
                        undefined // 去掉城市条件
                    );
                } else {
                    // 如果找不到司机ID，直接显示空抽屉
                    setDrawerData([]);
                    setDrawerVisible(true);
                }
            } else {
                // 区域维度 - 直接使用 record 中的 areaId 和 dspId（与 SLA 页面保持一致）
                const areaId = Number((record as any).areaId) || 0;
                // Hub DSP 维度下钻：必须使用“行上的 dspId”（你提到的 rowDspId 语义）
                const rowDspId = Number((record as any).dspId) || 0;
                const rowWarehouseId = Number((record as any).warehouseId) || 0;

                // Hub 视角：filters.viewMode 的语义是 “DSP视角/ WSH视角”
                const isHubWhsPerspective = isWhsDimension();
                const isHubDspPerspective = isDspDimension();

                // Hub 页面下钻参数设置：
                // 1. DSP 维度下钻：传递 hubId（树状节点）+ 当前行的 dspId
                // 2. WHS 维度下钻：传递 hubId（树状节点）+ 当前行的 whsId（warehouseId）
                const hubIdForQuery = currentWarehouse?.id ? parseInt(currentWarehouse.id) : undefined;
                const warehouseIdForQuery = (() => {
                    if (isHubDspPerspective) {
                        // Hub 页面 DSP 维度：不传 warehouseId，只传 hubId 和 dspId
                        return undefined;
                    }
                    if (isHubWhsPerspective) {
                        // Hub 页面 WHS 维度：传递当前行的 whsId（warehouseId）
                        return rowWarehouseId > 0 ? rowWarehouseId : undefined;
                    }
                    return undefined;
                })();

                const dspIdsForQuery = (() => {
                    if (isHubWhsPerspective) {
                        // Hub 页面 WHS 维度：不传 dspId，只传当前行的 whsId
                        return undefined;
                    }
                    // Hub 页面 DSP 维度：传当前行的 dspId
                    return rowDspId > 0 ? [rowDspId] : undefined;
                })();

                // Hub 页面（DSP/WHS 维度）：不传 areaId
                const areaIdsForQuery = undefined;

                // Hub 维度：WHS 需要 warehouseId，DSP 需要 dspId
                const canQueryByDimension = isHubWhsPerspective ? rowWarehouseId > 0 : rowDspId > 0;

                if (canQueryByDimension) {
                    // 获取状态字段
                    const statusField: string | undefined = deliveryEfficiencyStatusMap[type];
                    
                    await fetchDetailsByStatusField(
                        filters.taskDate.format('YYYY-MM-DD'),
                        statusField ? [statusField] : undefined,
                        pageRequest,
                        warehouseIdForQuery,
                        undefined,
                        hubIdForQuery,
                        dspIdsForQuery,
                        areaIdsForQuery,
                        undefined,
                        undefined
                    );
                } else {
                    // 维度关键字段缺失，直接显示空抽屉
                    setDrawerData([]);
                    setDrawerVisible(true);
                }
            }
        } catch (error) {
            console.error('获取详情数据失败:', error);
            // 出错时也显示抽屉，但数据为空
            setDrawerData([]);
            setDrawerVisible(true);
        }
    };

    // 未妥投分布点击下钻（不依赖具体某一行记录；按当前 viewMode + 筛选条件查询详情）
    const handleUndeliveredDistributionClick = async (
        type: 'undelivered' | 'unclaimed' | 'inDelivery' | 'returned' | 'damaged' | 'warehouseLost'
    ) => {
        const typeLabelMap: Record<string, string> = {
            undelivered: t('wms.delivery.dailyTaskMonitoring.undeliveredDistribution.undelivered'),
            unclaimed: t('wms.delivery.dailyTaskMonitoring.tableColumns.unpicked'),
            inDelivery: t('wms.delivery.dailyTaskMonitoring.tableColumns.inDelivery'),
            returned: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedReturned'),
            damaged: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedNotReturned'),
            warehouseLost: t('wms.delivery.dailyTaskMonitoring.tableColumns.warehouseLost')
        };
        const label = typeLabelMap[type] || type;

        if (filters.selectedDsps.length > 1 || filters.selectedAreas.length > 1 || filters.selectedCities.length > 1 || filters.selectedDrivers.length > 1) {
            setDrawerTitle(`${label} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')} (${t('wms.delivery.dailyTaskMonitoring.detailDrawer.multipleFilters')})`);
        } else {
            setDrawerTitle(`${label} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')}`);
        }

        // Hub 页面没有司机维度，统一使用区域维度
        setCurrentDetailType('area');

        const virtualRecord: AreaTaskData = {
            id: 0,
            area: '',
            dsp: '',
            warehouseName: currentWarehouse?.name || undefined,
            allocatedParcels: 0,
            pickedUpFromWarehouse: 0,
            warehouseLost: 0,
            inDelivery: 0,
            onTimeDelivery: 0,
            overdueDelivery: 0,
            deliveredCount: 0,
            unclaimedCount: 0,
            noAscanDelivery: 0,
            transferredIn: 0,
            transferredOut: 0,
            returned: 0,
            damaged: 0,
            deliveryRate: 0,
            failedDeliveryRate: 0
        };

        setCurrentDetailRecord(virtualRecord);
        const statusField = type === 'undelivered' ? 'isUndelivered' : getStatusField(type, t);
        
        // 如果是未妥投类型，初始化状态筛选为包含5个状态：未领取、派送中、派送失败已回仓、派送失败未回仓、仓内疑似丢失
        if (type === 'undelivered') {
            setDetailStatusTypes(['unclaimed', 'inDelivery', 'returned', 'damaged', 'warehouseLost']);
            // 更新 lastDetailContext，使用 statusFields 而不是 statusField
            const undeliveredStatusFields = ['isUnclaimed', 'isInDelivery', 'isReturned', 'isDamaged', 'isWarehouseLost'];
            setLastDetailContext({ type, record: virtualRecord, filters: { ...filters }, statusField: undefined, statusFields: undeliveredStatusFields });
        } else {
            setDetailStatusTypes(statusTypeOptions.some(opt => opt.value === type) ? [type] : []);
            setLastDetailContext({ type, record: virtualRecord, filters: { ...filters }, statusField });
        }

        const initialDetailFilters = {
            selectedWarehouses: [],
            selectedDsps: filters.selectedDsps,
            selectedAreas: filters.selectedAreas,
            selectedDrivers: filters.selectedDrivers
        };
        skipDetailFiltersEffectRef.current = true;
        setDetailFilters(initialDetailFilters);

        setDrawerColumns(getColumnsByType(type, 'area'));
        setDrawerVisible(true);

        const pageRequest = {
            page: 1,
            size: 10,
            sortBy: detailSortConfig.sortBy,
            sortDirection: detailSortConfig.sortDirection
        };

        try {
            // Hub 页面未妥投分布下钻：传递 hubId（树状节点）
            const hubIdForQuery = currentWarehouse?.id ? parseInt(currentWarehouse.id) : undefined;
            
            // 如果是未妥投类型，使用状态筛选对应的 statusFields
            let statusFieldsForQuery: string[] | undefined;
            if (type === 'undelivered') {
                // 未妥投包含5个状态：未领取、派送中、派送失败已回仓、派送失败未回仓、仓内疑似丢失
                statusFieldsForQuery = ['isUnclaimed', 'isInDelivery', 'isReturned', 'isDamaged', 'isWarehouseLost'];
            } else {
                statusFieldsForQuery = statusField ? [statusField] : undefined;
            }
            
            // 将 detailFilters 中的筛选条件转换为ID（与 refetchDetailData 逻辑保持一致）
            const selectedDspIds = initialDetailFilters.selectedDsps.length > 0
                ? (() => {
                    const ids = initialDetailFilters.selectedDsps.map(dspName => {
                        const dsp = dsps.find((d: any) => d.name === dspName);
                        return dsp ? dsp.id : null;
                    }).filter(id => id !== null) as number[];
                    return ids.length > 0 ? ids : undefined;
                })()
                : undefined;
            
            const selectedAreaIds = initialDetailFilters.selectedAreas.length > 0
                ? (() => {
                    const ids = initialDetailFilters.selectedAreas.map(areaName => {
                        const area = areas.find((a: { id: number; name: string }) => a.name === areaName);
                        return area ? area.id : null;
                    }).filter((id): id is number => id !== null);
                    return ids.length > 0 ? ids : undefined;
                })()
                : undefined;
            
            const selectedDriverIds = toDriverIds(initialDetailFilters.selectedDrivers);
            
            // Hub 页面只有区域维度，没有司机维度
            await fetchDetailsByStatusField(
                filters.taskDate.format('YYYY-MM-DD'),
                statusFieldsForQuery,
                pageRequest,
                undefined,
                undefined,
                hubIdForQuery,
                selectedDspIds,
                selectedAreaIds,
                selectedDriverIds,
                undefined
            );
        } catch (error) {
            console.error('未妥投分布下钻获取详情失败:', error);
            setDrawerData([]);
            setDrawerVisible(true);
        }
    };

    // 当日派送进展下钻（应派件量、实际领取、已妥投）：按当前 viewMode + 筛选条件查询详情，不依赖具体某一行
    const handleDeliveryProgressClick = async (
        type: 'allocatedParcels' | 'pickedUpFromWarehouse' | 'delivered'
    ) => {
        const typeLabelMap: Record<string, string> = {
            allocatedParcels: t('wms.delivery.dailyTaskMonitoring.tableColumns.scheduledParcels'),
            pickedUpFromWarehouse: t('wms.delivery.dailyTaskMonitoring.summary.actualPickedUp'),
            delivered: t('wms.delivery.dailyTaskMonitoring.summary.deliveredParcels')
        };
        const statusFieldMap: Record<string, string> = {
            allocatedParcels: 'isAllocatedParcels',
            pickedUpFromWarehouse: 'isPickedUpFromWarehouse',
            delivered: 'isDelivered'
        };
        const label = typeLabelMap[type] || type;
        const statusField = statusFieldMap[type];

        if (filters.selectedDsps.length > 1 || filters.selectedAreas.length > 1 || filters.selectedCities.length > 1 || filters.selectedDrivers.length > 1) {
            setDrawerTitle(`${label} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')} (${t('wms.delivery.dailyTaskMonitoring.detailDrawer.multipleFilters')})`);
        } else {
            setDrawerTitle(`${label} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')}`);
        }

        // Hub 页面没有司机维度，统一使用区域维度
        setCurrentDetailType('area');

        const virtualRecord: AreaTaskData = {
            id: 0,
            area: '',
            dsp: '',
            warehouseName: currentWarehouse?.name || undefined,
            allocatedParcels: 0,
            pickedUpFromWarehouse: 0,
            warehouseLost: 0,
            inDelivery: 0,
            onTimeDelivery: 0,
            overdueDelivery: 0,
            deliveredCount: 0,
            unclaimedCount: 0,
            noAscanDelivery: 0,
            transferredIn: 0,
            transferredOut: 0,
            returned: 0,
            damaged: 0,
            deliveryRate: 0,
            failedDeliveryRate: 0
        };

        setCurrentDetailRecord(virtualRecord);
        setDetailStatusTypes(statusTypeOptions.some(opt => opt.value === type) ? [type] : []);
        setLastDetailContext({ type, record: virtualRecord, filters: { ...filters }, statusField });

        const initialDetailFilters = {
            selectedWarehouses: [],
            selectedDsps: filters.selectedDsps,
            selectedAreas: filters.selectedAreas,
            selectedDrivers: filters.selectedDrivers
        };
        skipDetailFiltersEffectRef.current = true;
        setDetailFilters(initialDetailFilters);

        setDrawerColumns(getColumnsByType(type, 'area'));
        setDrawerVisible(true);

        const pageRequest = {
            page: 1,
            size: 10,
            sortBy: detailSortConfig.sortBy,
            sortDirection: detailSortConfig.sortDirection
        };

        try {
            const hubIdForQuery = currentWarehouse?.id ? parseInt(currentWarehouse.id) : undefined;
            const selectedDspIds = initialDetailFilters.selectedDsps.length > 0
                ? (() => {
                    const ids = initialDetailFilters.selectedDsps.map(dspName => {
                        const dsp = dsps.find((d: any) => d.name === dspName);
                        return dsp ? dsp.id : null;
                    }).filter(id => id !== null) as number[];
                    return ids.length > 0 ? ids : undefined;
                })()
                : undefined;
            const selectedAreaIds = initialDetailFilters.selectedAreas.length > 0
                ? (() => {
                    const ids = initialDetailFilters.selectedAreas.map(areaName => {
                        const area = areas.find((a: { id: number; name: string }) => a.name === areaName);
                        return area ? area.id : null;
                    }).filter((id): id is number => id !== null);
                    return ids.length > 0 ? ids : undefined;
                })()
                : undefined;
            const selectedDriverIds = toDriverIds(initialDetailFilters.selectedDrivers);

            await fetchDetailsByStatusField(
                filters.taskDate.format('YYYY-MM-DD'),
                [statusField],
                pageRequest,
                undefined,
                undefined,
                hubIdForQuery,
                selectedDspIds,
                selectedAreaIds,
                selectedDriverIds,
                undefined
            );
        } catch (error) {
            console.error('当日派送进展下钻获取详情失败:', error);
            setDrawerData([]);
            setDrawerVisible(true);
        }
    };

    // 配送效率状态映射 - 对应数据库字段名（与handleDetailClick中的定义保持一致）
    const deliveryEfficiencyStatusMap: { [key: string]: string } = {
        'on_time_delivery': 'isOnTimeDelivery',    // 准时妥投
        'overdue_delivery': 'isOverdueDelivery',   // 超时妥投
        'transferred_in': 'isTransferredIn',       // 当日转入
        'transferred_out': 'isTransferredOut',     // 当日转出
        'inDelivery': 'isInDelivery',              // 派送中
        'damaged': 'isDamaged',                    // 失败未回仓
        'allocatedParcels': 'isAllocatedParcels',  // 线路应派总量
        'pickedUpFromWarehouse': 'isPickedUpFromWarehouse',  // 司机实领
        'warehouseLost': 'isWarehouseLost',        // 仓内丢失
        'returned': 'isReturned',                   // 失败已回仓
        'unclaimed': 'isUnclaimed',              // 未领取
        'no_ascan_delivery': 'noAscanDelivery'      // 无Ascan妥投
    };

    // 根据详情筛选条件重新查询数据
    const refetchDetailData = async () => {
        if (!lastDetailContext) return;

        const { type, record } = lastDetailContext;
        
        // 将详情筛选条件转换为ID
        // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
        const detailDsps = dsps;
        const selectedDspIds = detailFilters.selectedDsps.length > 0
            ? (() => {
                const ids = detailFilters.selectedDsps.map(dspName => {
                    const dsp = detailDsps.find((d: any) => d.name === dspName);
                    return dsp ? dsp.id : null;
                }).filter(id => id !== null) as number[];
                // 如果转换后是空数组，返回 undefined
                return ids.length > 0 ? ids : undefined;
            })()
            : undefined;

        const selectedAreaIds = detailFilters.selectedAreas.length > 0
            ? (() => {
                const ids = detailFilters.selectedAreas.map(areaName => {
                    const area = areas.find((a: { id: number; name: string }) => a.name === areaName);
                    return area ? area.id : null;
                }).filter((id): id is number => id !== null);
                // 如果转换后是空数组，返回 undefined
                return ids.length > 0 ? ids : undefined;
            })()
            : undefined;

        const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);

        // 构建分页请求
        const pageRequest = {
            page: detailPagination?.page || 1,
            size: detailPagination?.size || 10,
            sortBy: detailSortConfig.sortBy,
            sortDirection: detailSortConfig.sortDirection
        };

        try {
            // 根据 detailStatusTypes 获取状态字段（如果用户删掉了状态筛选，detailStatusTypes 为空数组，则查询所有状态）
            const statusFields: string[] | undefined = detailStatusTypes.length > 0
                ? detailStatusTypes.map((t) => {
                    const deliveryEfficiencyStatusMap: { [key: string]: string } = {
                        'on_time_delivery': 'isOnTimeDelivery',
                        'overdue_delivery': 'isOverdueDelivery',
                        'transferred_in': 'isTransferredIn',
                        'transferred_out': 'isTransferredOut',
                        'allocatedParcels': 'isAllocatedParcels',
                        'pickedUpFromWarehouse': 'isPickedUpFromWarehouse',
                        'delivered': 'isDelivered',
                        'unclaimed': 'isUnclaimed',
                        'inDelivery': 'isInDelivery',
                        'returned': 'isReturned',
                        'damaged': 'isDamaged',
                        'warehouseLost': 'isWarehouseLost'
                    };
                    return deliveryEfficiencyStatusMap[t];
                }).filter((v): v is string => typeof v === 'string' && v.length > 0)
                : undefined; // 如果 detailStatusTypes 为空，传递 undefined 查询所有状态

            // Hub 页面的 DSP 和 WHS 维度：传递当前树状节点的 hubId
            const hubIdForRefetch = (() => {
                if (isDspDimension() || isWhsDimension()) {
                    const currentHubId = currentWarehouse?.siteType === SiteType.HUB
                        ? currentWarehouse.id
                        : undefined;
                    return currentHubId ? parseInt(currentHubId) : undefined;
                }
                return undefined;
            })();
            const warehouseIdsForRefetch = (() => {
                if (isWhsDimension() && detailFilters.selectedWarehouses?.length) {
                    const list = structuralWarehouses.map(whs => ({ id: String(whs.id), name: whs.name }));
                    const ids = (detailFilters.selectedWarehouses || []).map(name => {
                        const w = list.find(x => x.name === name);
                        return w?.id ? parseInt(w.id, 10) : null;
                    }).filter((id): id is number => id !== null && !isNaN(id));
                    return ids.length > 0 ? ids : undefined;
                }
                return undefined;
            })();
            const warehouseIdForRefetch = warehouseIdsForRefetch?.length === 1 ? warehouseIdsForRefetch[0] : undefined;
            
            await fetchDetailsByStatusField(
                filters.taskDate.format('YYYY-MM-DD'),
                statusFields,
                pageRequest,
                warehouseIdForRefetch,
                warehouseIdsForRefetch,
                hubIdForRefetch,
                selectedDspIds,
                selectedAreaIds,
                selectedDriverIds,
                undefined
            );
        } catch (error) {
            console.error('重新查询详情数据失败:', error);
        }
    };

    // Hub 页只有 dsp / whs 两个视角（都走区域口径的表），列表里没有转入列，
    // 故不提供 transferred_in —— 提供了也没有任何列能对应上，是纯误导项。
    // 司机维度只在 Daily 页存在，那边按 viewMode 判断后才给这一项。
    const statusTypeOptions = useMemo(() => {
        return [
            { value: 'allocatedParcels', label: t('wms.delivery.dailyTaskMonitoring.areaColumns.allocatedParcels') },
            { value: 'pickedUpFromWarehouse', label: t('wms.delivery.dailyTaskMonitoring.summary.actualPickedUp') },
            { value: 'delivered', label: t('wms.delivery.dailyTaskMonitoring.tableColumns.delivered') },
            { value: 'unclaimed', label: t('wms.delivery.dailyTaskMonitoring.tableColumns.unpicked') },
            { value: 'inDelivery', label: t('wms.delivery.dailyTaskMonitoring.tableColumns.inDelivery') },
            { value: 'returned', label: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedReturned') },
            { value: 'damaged', label: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedNotReturned') },
            { value: 'warehouseLost', label: t('wms.delivery.dailyTaskMonitoring.tableColumns.warehouseLost') }
        ];
    }, [t]);

    const handleDetailStatusTypeChange = useCallback(async (nextTypes: string[]) => {
        if (!drawerVisible || !lastDetailContext) return;

        const nextTypesSafe = nextTypes || [];
        setDetailStatusTypes(nextTypesSafe);
        const statusFields = nextTypesSafe
            .map((t) => deliveryEfficiencyStatusMap[t])
            .filter((v): v is string => typeof v === 'string' && v.length > 0);

        // 更新抽屉标题（沿用“司机/区域”下钻命名方式）
        const labelMap: Record<string, string> = {
            allocatedParcels: t('wms.delivery.dailyTaskMonitoring.areaColumns.allocatedParcels'),
            transferred_in: t('wms.delivery.dailyTaskMonitoring.areaColumns.transferInToday'),
            pickedUpFromWarehouse: t('wms.delivery.dailyTaskMonitoring.summary.actualPickedUp'),
            delivered: t('wms.delivery.dailyTaskMonitoring.tableColumns.delivered'),
            unclaimed: t('wms.delivery.dailyTaskMonitoring.tableColumns.unpicked'),
            inDelivery: t('wms.delivery.dailyTaskMonitoring.tableColumns.inDelivery'),
            returned: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedReturned'),
            damaged: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedNotReturned'),
            warehouseLost: t('wms.delivery.dailyTaskMonitoring.tableColumns.warehouseLost')
        };
        const nextLabel = nextTypesSafe.length === 1 ? (labelMap[nextTypesSafe[0]] || nextTypesSafe[0]) : t('wms.delivery.dailyTaskMonitoring.detailColumns.status');

        const titlePrefix = (() => {
            // Hub 页 WHS/DSP 视角：根据筛选条件决定标题显示内容
            if (isWhsDimension()) {
                // WHS 维度：如果用户选择了仓库，显示仓库名称；否则只显示当前 hub 名称（不显示 record.warehouseName）
                if (detailFilters.selectedWarehouses && detailFilters.selectedWarehouses.length > 0) {
                    return detailFilters.selectedWarehouses[0];
                }
                return currentWarehouse?.name || '';
            }
            // DSP 维度：如果用户选择了 DSP，显示 DSP 名称；否则显示 hub 名称（与 WHS 维度一致）
            if (detailFilters.selectedDsps && detailFilters.selectedDsps.length > 0) {
                return detailFilters.selectedDsps[0];
            }
            return currentWarehouse?.name || '';
        })();

        setDrawerTitle(titlePrefix
            ? `${titlePrefix} - ${nextLabel} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')}`
            : `${nextLabel} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')}`);

        // 更新列（只展示当前状态对应的列）
        const detailType = currentDetailType === 'driver' ? 'driver' : 'area';
        setDrawerColumns(nextTypesSafe.length === 1 ? getColumnsByType(nextTypesSafe[0], detailType) : getColumnsByType('status', detailType));

        // 更新上下文（用于导出/分页/排序）
        setLastDetailContext((prev) => prev ? ({
            ...prev,
            // 多选时不改变 type（避免误导）；单选时更新 type
            ...(nextTypesSafe.length === 1 ? { type: nextTypesSafe[0] } : {}),
            // 单选兼容旧字段；多选走 statusFields
            statusField: nextTypesSafe.length === 1 ? deliveryEfficiencyStatusMap[nextTypesSafe[0]] : undefined,
            statusFields: statusFields
        }) : prev);

        // 切换状态时回到第一页重新查（复用 refetchDetailData 的逻辑）
        // 这里直接调用查询，避免等待 setState 导致的时序问题
        // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
        const detailDsps = dsps;
        const selectedDspIds = detailFilters.selectedDsps.length > 0
            ? (() => {
                const ids = detailFilters.selectedDsps.map(dspName => {
                    const dsp = detailDsps.find((d: any) => d.name === dspName);
                    return dsp ? dsp.id : null;
                }).filter(id => id !== null) as number[];
                // 如果转换后是空数组，返回 undefined
                return ids.length > 0 ? ids : undefined;
            })()
            : undefined;

        const detailAreas = areas;
        const selectedAreaIds = detailFilters.selectedAreas.length > 0
            ? (() => {
                const ids = detailFilters.selectedAreas.map(areaName => {
                    const area = detailAreas.find((a: { id: number; name: string }) => a.name === areaName);
                    return area ? area.id : null;
                }).filter((id): id is number => id !== null);
                return ids.length > 0 ? ids : undefined;
            })()
            : undefined;

        const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);

        const pageRequest = {
            page: 1,
            size: detailPagination?.size || 10,
            sortBy: detailSortConfig.sortBy,
            sortDirection: detailSortConfig.sortDirection
        };

        const isHubDspPerspective = isDspDimension();
        const isHubWhsPerspective = isWhsDimension();

        // 根据筛选条件决定是否传 warehouseId
        const getWarehouseIdForStatusChange = (): string | number | undefined => {
            // Hub DSP 维度：不传 warehouseId
            if (isHubDspPerspective) {
                return undefined;
            }

            // Hub WHS 维度：仅当仓库下拉选了仓库时传 whsId
            if (isHubWhsPerspective && detailFilters.selectedWarehouses && detailFilters.selectedWarehouses.length > 0) {
                const warehousesForQuery = structuralWarehouses.map(whs => ({ id: String(whs.id), name: whs.name }));

                const selectedWarehouse = warehousesForQuery.find(whs =>
                    detailFilters.selectedWarehouses?.includes(whs.name)
                );
                if (selectedWarehouse && selectedWarehouse.id) {
                    return parseInt(selectedWarehouse.id);
                }
            }
            return undefined;
        };

        const warehouseIdForStatusChange = getWarehouseIdForStatusChange();
        const warehouseIdsForStatusChange = (() => {
            if (isWhsDimension() && detailFilters.selectedWarehouses?.length) {
                const list = structuralWarehouses.map(whs => ({ id: String(whs.id), name: whs.name }));
                const ids = (detailFilters.selectedWarehouses || []).map(name => {
                    const w = list.find(x => x.name === name);
                    return w?.id ? parseInt(w.id, 10) : null;
                }).filter((id): id is number => id !== null && !isNaN(id));
                return ids.length > 0 ? ids : undefined;
            }
            return undefined;
        })();

        try {
            const hubIdForStatusChange = currentWarehouse?.siteType === SiteType.HUB
                ? parseInt(currentWarehouse.id)
                : undefined;
            await fetchDetailsByStatusField(
                filters.taskDate.format('YYYY-MM-DD'),
                statusFields,
                pageRequest,
                warehouseIdForStatusChange,
                warehouseIdsForStatusChange,
                hubIdForStatusChange,
                selectedDspIds,
                selectedAreaIds,
                selectedDriverIds,
                undefined
            );
        } catch (e) {
            console.error('切换状态重新查询详情失败:', e);
        }
    }, [
        drawerVisible,
        lastDetailContext,
        t,
        currentDetailType,
        detailFilters,
        areas,
        drivers,
        dsps,
        detailPagination?.size,
        detailSortConfig.sortBy,
        detailSortConfig.sortDirection,
        filters.viewMode,
        filters.taskDate,
        warehouseId,
        structuralWarehouses,
        currentWarehouse,
        fetchDetailsByStatusField,
        fetchDetailsByStatusFieldForDriver
    ]);

    // 监听详情筛选条件变化，重新查询数据
    useEffect(() => {
        // 如果标记为跳过，则重置标记并返回，不执行查询
        if (skipDetailFiltersEffectRef.current) {
            skipDetailFiltersEffectRef.current = false;
            return;
        }
        
        if (drawerVisible && lastDetailContext) {
            // 筛选条件改变时，重置到第一页并重新查询
            // 注意：这里不直接调用 refetchDetailData，而是通过修改分页状态来触发查询
            // 因为 refetchDetailData 依赖 detailPagination，需要先更新分页状态
            const pageRequest = {
                page: 1,
                size: detailPagination?.size || 10,
                sortBy: detailSortConfig.sortBy,
                sortDirection: detailSortConfig.sortDirection
            };
            
            // 将详情筛选条件转换为ID
            // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
            const detailDsps = dsps;
            const selectedDspIds = detailFilters.selectedDsps.length > 0
                ? (() => {
                    const ids = detailFilters.selectedDsps.map(dspName => {
                        const dsp = detailDsps.find((d: any) => d.name === dspName);
                        return dsp ? dsp.id : null;
                    }).filter(id => id !== null) as number[];
                    // 如果转换后是空数组，返回 undefined
                    return ids.length > 0 ? ids : undefined;
                })()
                : undefined;

            // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
            const detailAreas = areas;
            const selectedAreaIds = detailFilters.selectedAreas.length > 0
                ? (() => {
                    const ids = detailFilters.selectedAreas.map(areaName => {
                        const area = detailAreas.find((a: { id: number; name: string }) => a.name === areaName);
                        return area ? area.id : null;
                    }).filter((id): id is number => id !== null);
                    // 如果转换后是空数组，返回 undefined
                    return ids.length > 0 ? ids : undefined;
                })()
                : undefined;

            const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);

            // 根据 detailStatusTypes 获取状态字段（如果用户删掉了状态筛选，detailStatusTypes 为空数组，则查询所有状态）
            const statusFields = detailStatusTypes.length > 0
                ? detailStatusTypes.map((t) => {
                    const deliveryEfficiencyStatusMap: { [key: string]: string } = {
                        'on_time_delivery': 'isOnTimeDelivery',
                        'overdue_delivery': 'isOverdueDelivery',
                        'transferred_in': 'isTransferredIn',
                        'transferred_out': 'isTransferredOut',
                        'allocatedParcels': 'isAllocatedParcels',
                        'pickedUpFromWarehouse': 'isPickedUpFromWarehouse',
                        'delivered': 'isDelivered',
                        'unclaimed': 'isUnclaimed',
                        'inDelivery': 'isInDelivery',
                        'returned': 'isReturned',
                        'damaged': 'isDamaged',
                        'warehouseLost': 'isWarehouseLost'
                    };
                    return deliveryEfficiencyStatusMap[t];
                }).filter((v): v is string => typeof v === 'string' && v.length > 0)
                : undefined; // 如果 detailStatusTypes 为空，传递 undefined 查询所有状态

            // Hub 页面的 DSP 和 WHS 维度：传递当前树状节点的 hubId
            const hubIdForEffect = (() => {
                if (isDspDimension() || isWhsDimension()) {
                    const currentHubId = currentWarehouse?.siteType === SiteType.HUB
                        ? currentWarehouse.id
                        : undefined;
                    return currentHubId ? parseInt(currentHubId) : undefined;
                }
                return undefined;
            })();
            const warehouseIdsForEffect = (() => {
                if (isWhsDimension() && detailFilters.selectedWarehouses?.length) {
                    const list = structuralWarehouses.map(whs => ({ id: String(whs.id), name: whs.name }));
                    const ids = (detailFilters.selectedWarehouses || []).map(name => {
                        const w = list.find(x => x.name === name);
                        return w?.id ? parseInt(w.id, 10) : null;
                    }).filter((id): id is number => id !== null && !isNaN(id));
                    return ids.length > 0 ? ids : undefined;
                }
                return undefined;
            })();
            const warehouseIdForEffect = warehouseIdsForEffect?.length === 1 ? warehouseIdsForEffect[0] : undefined;

            const fetchData = async () => {
                try {
                    await fetchDetailsByStatusField(
                        filters.taskDate.format('YYYY-MM-DD'),
                        statusFields,
                        pageRequest,
                        warehouseIdForEffect,
                        warehouseIdsForEffect,
                        hubIdForEffect,
                        selectedDspIds,
                        selectedAreaIds,
                        selectedDriverIds,
                        undefined
                    );
                } catch (error) {
                    console.error('重新查询详情数据失败:', error);
                }
            };
            
            // 更新标题：根据筛选条件更新标题前缀和状态标签
            if (lastDetailContext) {
                // 使用 detailStatusTypes 来决定状态标签，而不是 lastDetailContext.type
                const labelMap: Record<string, string> = {
                    allocatedParcels: t('wms.delivery.dailyTaskMonitoring.areaColumns.allocatedParcels'),
                    transferred_in: t('wms.delivery.dailyTaskMonitoring.areaColumns.transferInToday'),
                    pickedUpFromWarehouse: t('wms.delivery.dailyTaskMonitoring.summary.actualPickedUp'),
                    delivered: t('wms.delivery.dailyTaskMonitoring.tableColumns.delivered'),
                    unclaimed: t('wms.delivery.dailyTaskMonitoring.tableColumns.unpicked'),
                    inDelivery: t('wms.delivery.dailyTaskMonitoring.tableColumns.inDelivery'),
                    returned: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedReturned'),
                    damaged: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedNotReturned'),
                    warehouseLost: t('wms.delivery.dailyTaskMonitoring.tableColumns.warehouseLost')
                };
                const typeLabel = detailStatusTypes.length === 1
                    ? (labelMap[detailStatusTypes[0]] || detailStatusTypes[0])
                    : t('wms.delivery.dailyTaskMonitoring.detailColumns.status');

                // Hub 页面：根据筛选条件决定标题前缀
                const titlePrefix = (() => {
                    if (isWhsDimension()) {
                        // WHS 维度：如果用户选择了仓库，显示仓库名称；否则显示当前 hub 名称
                        if (detailFilters.selectedWarehouses && detailFilters.selectedWarehouses.length > 0) {
                            return detailFilters.selectedWarehouses[0];
                        }
                        return currentWarehouse?.name || '';
                    }
                    // DSP 维度：如果用户选择了 DSP，显示 DSP 名称；否则显示 hub 名称（与 WHS 维度一致）
                    if (detailFilters.selectedDsps && detailFilters.selectedDsps.length > 0) {
                        return detailFilters.selectedDsps[0];
                    }
                    return currentWarehouse?.name || '';
                })();

                setDrawerTitle(titlePrefix
                    ? `${titlePrefix} - ${typeLabel} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')}`
                    : `${typeLabel} - ${t('wms.delivery.dailyTaskMonitoring.detailDrawer.detailPage')}`);
            }
            
            fetchData();
        }
    }, [detailFilters]);

    // 监听详情数据变化，更新抽屉数据（仅当响应与当前请求匹配时更新，避免竞态导致显示错误条数）
    useEffect(() => {
        if (!newDetailData) return;
        if (detailDataRequestId != null && detailDataRequestId !== detailRequestIdRef.current) return;
        const convertedData: DetailData[] = newDetailData.map(item => {
            const warehouseName = structuralWarehouses.find(w => w.id != null && String(w.id) === String(item.warehouseId))?.name ?? '';
            return {
            id: item.id ? String(item.id) : undefined,
            warehouse_name: warehouseName,
            dsp_name: item.dspName || '',
            area_name: item.areaName || '',
            driver_id: item.driverId || 0,
            driver_name: item.driverName && item.driverName.trim().length > 0 ? item.driverName : '-',
            task_date: item.taskDate || '',
            tracking_number: item.trackingNumber,
            status: item.overallStatusText || getStatusText(item.currentStatus || 0),
            order_id: item.orderId || 0,
            last_failure_reason: item.lastFailureReason || '',
            exception_reason_code: item.exceptionReasonCode || undefined,
            latest_physical_tracking_updated_at: item.latestPhysicalTrackingUpdatedAt || ''
        };
        });
        setDrawerData(convertedData);
        // 不需要重复设置 setDrawerVisible(true)，因为已经在 handleDetailClick 中设置了
    }, [newDetailData, detailDataRequestId, currentDetailType, structuralWarehouses]);

    // 使用 useRef 跟踪上一次查询的参数，避免重复查询
    const lastQueryParamsRef = React.useRef<string>('');

    // 记录最近一次列表查询实际使用的筛选条件，供汇总下载复用，保证「与页面一模一样」
    const currentSummaryDownloadArgsRef = React.useRef<{
        taskDate: string;
        viewMode: string;
        hubId?: number;
        warehouseIds?: number[];
        dspIds?: number[];
        cityNames?: string[];
        sortBy?: string;
        sortDirection?: 'ASC' | 'DESC';
    } | null>(null);

    // 初始化查询和分页查询（包含筛选条件）
    useEffect(() => {
        // DSP 已选名称必须能映射为 id 才能下发筛选，否则会退化成「不带该条件」的查询、静默查出全量。
        // 司机不需要这道门禁：下拉 value 本身就是 accountId，不存在未映射态。
        const hasUnmappedSelection =
            filters.selectedDsps.some((name: string) => !dsps.some((d: { id: number; name: string }) => d.name === name));
        if (hasUnmappedSelection) {
            return;
        }

        const computedSelectedDspIds = filters.selectedDsps.length > 0
            ? filters.selectedDsps
                .map((dspName: string) => dsps.find((d: { id: number; name: string }) => d.name === dspName)?.id ?? null)
                .filter((id: number | null): id is number => id !== null)
            : undefined;

        const computedSelectedDriverIds = toDriverIds(filters.selectedDrivers);
        
        // Hub 页面无 DSP Manager 场景（DSP 用户走 /delivery 独立路由），仅按已选筛选下发
        const finalDspIds = computedSelectedDspIds && computedSelectedDspIds.length > 0 ? computedSelectedDspIds : undefined;
        
        const selectedAreaIds = filters.selectedAreas.length > 0 
            ? convertNamesToIds(filters.selectedAreas, areas)
            : undefined;

        const selectedWarehouseIdsForQuery = (filters.selectedWarehouses && filters.selectedWarehouses.length > 0)
            ? filters.selectedWarehouses
                .map((id: string) => Number(id))
                .filter((id: number) => Number.isFinite(id) && id > 0)
            : undefined;
        
        // 构建查询参数的唯一标识，用于避免重复查询
        const queryParamsKey = JSON.stringify({
            viewMode: filters.viewMode,
            taskDate: taskDateString,
            currentPage,
            pageSize,
            sortBy: sortConfig.sortBy,
            sortDirection: sortConfig.sortDirection,
            warehouseId: currentWarehouse?.id,
            selectedWarehouseIdsForQuery,
            finalDspIds,
            selectedAreaIds,
            computedSelectedDriverIds,
            selectedCities: filters.selectedCities
        });
        
        // 如果查询参数没有变化，跳过查询
        if (lastQueryParamsRef.current === queryParamsKey) {
            return;
        }
        
        // 更新查询参数标识
        lastQueryParamsRef.current = queryParamsKey;
        
        // Hub 页面：无论 viewMode 是什么，都使用区域汇总查询
        // DSP 维度：传 dspIds，不传 areaIds 和 warehouseIds
        // WHS 维度：传 warehouseIds，不传 areaIds 和 dspIds
        const baseParams = getAreaQueryParams();
        const isHubDspDimension = isDspDimension();
        const isHubWhsDimension = isWhsDimension();

        const areaPageRequest = {
            taskDate: taskDateString,
            ...baseParams,
            warehouseIds: isHubWhsDimension && selectedWarehouseIdsForQuery && selectedWarehouseIdsForQuery.length > 0 ? selectedWarehouseIdsForQuery : undefined,
            dspIds: isHubDspDimension && finalDspIds && finalDspIds.length > 0 ? finalDspIds : undefined,
            areaIds: undefined, // Hub 页面的 DSP 和 WHS 维度都不传 areaIds
            cities: filters.selectedCities.length > 0 ? filters.selectedCities : undefined,
            dimensionType: getCurrentDimensionType(),
            pageRequest: {
                page: currentPage,
                size: pageSize,
                sortBy: sortConfig.sortBy || 'id',
                sortDirection: sortConfig.sortDirection || 'ASC' as 'ASC' | 'DESC'
            }
        };

        currentSummaryDownloadArgsRef.current = {
                taskDate: taskDateString,
                viewMode: filters.viewMode,
                hubId: (baseParams as { hubId?: number }).hubId,
                warehouseIds: isHubWhsDimension && selectedWarehouseIdsForQuery && selectedWarehouseIdsForQuery.length > 0 ? selectedWarehouseIdsForQuery : undefined,
                dspIds: isHubDspDimension && finalDspIds && finalDspIds.length > 0 ? finalDspIds : undefined,
                cityNames: filters.selectedCities.length > 0 ? filters.selectedCities : undefined,
                sortBy: sortConfig.sortBy,
                sortDirection: sortConfig.sortDirection,
            };handleAreaFilter(areaPageRequest);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, filters.viewMode, taskDateString, JSON.stringify(filters.selectedWarehouses), JSON.stringify(filters.selectedDsps), JSON.stringify(filters.selectedAreas), JSON.stringify(filters.selectedCities), JSON.stringify(filters.selectedDrivers), sortConfig.sortBy, sortConfig.sortDirection, currentWarehouse?.id, dsps.length]); // 使用稳定的 taskDateString 而不是 filters.taskDate.format('YYYY-MM-DD')

    // 分页处理函数：仅更新状态，具体请求交给 useEffect 统一处理
    const handlePageChange = (page: number, newPageSize?: number) => {
        setCurrentPage(page);
        if (newPageSize && newPageSize !== pageSize) {
            setPageSize(newPageSize);
        }
    };

    // 详情分页处理函数
    const handleDetailPageChange = async (page: number, pageSize?: number) => {
        // 使用 drawerVisible 和 lastDetailContext 来判断，而不是 currentDetailRecord
        // 因为分页切换时可能 currentDetailRecord 已经被清空，但查询上下文还在
        if (!drawerVisible || !lastDetailContext) return;
        
        const pageRequest = {
            page,
            size: pageSize || 10,
            sortBy: detailSortConfig.sortBy,
            sortDirection: detailSortConfig.sortDirection
        };

        // 明细传参以下拉筛选实际选择为准：WHS 维度多选时传 warehouseIds 数组，单选/未选时不传或传单值
        const getWarehouseIdsForQuery = (): number[] | undefined => {
            if (isDspDimension()) return undefined;
            if (!isWhsDimension() || !detailFilters.selectedWarehouses?.length) return undefined;
            const currentHubId = currentWarehouse?.siteType === SiteType.HUB ? currentWarehouse.id : undefined;
            if (!currentHubId) return undefined;
            const warehousesForQuery = structuralWarehouses.map(whs => ({ id: String(whs.id), name: whs.name }));
            const ids = (detailFilters.selectedWarehouses || [])
                .map(name => {
                    const whs = warehousesForQuery.find(w => w.name === name);
                    return whs?.id ? parseInt(whs.id, 10) : null;
                })
                .filter((id): id is number => id !== null && !isNaN(id));
            return ids.length > 0 ? ids : undefined;
        };

        const warehouseIdsForQuery = getWarehouseIdsForQuery();
        const warehouseIdForQuery = (warehouseIdsForQuery?.length === 1 ? warehouseIdsForQuery[0] : undefined) as string | number | undefined;
        // Hub 页面：传递 hubId（树状节点）
        const hubIdForQuery = currentWarehouse?.siteType === SiteType.HUB
            ? parseInt(currentWarehouse.id)
            : undefined;
        
        try {
            // Hub 页面只有区域维度 - 使用详情筛选条件
            const selectedDspIds = detailFilters.selectedDsps.length > 0
                ? (() => {
                    const ids = detailFilters.selectedDsps.map(dspName => {
                        const dsp = dsps.find((d: any) => d.name === dspName);
                        return dsp ? dsp.id : null;
                    }).filter(id => id !== null) as number[];
                    // 如果转换后是空数组，返回 undefined
                    return ids.length > 0 ? ids : undefined;
                })()
                : undefined;

            const selectedAreaIds = detailFilters.selectedAreas.length > 0
                ? detailFilters.selectedAreas.map(areaName => {
                    const area = areas.find((a: { id: number; name: string }) => a.name === areaName);
                    return area ? area.id : null;
                }).filter((id): id is number => id !== null)
                : undefined;

            // 详情查询不使用城市条件
            const selectedCities = undefined;

            // 根据 detailStatusTypes 获取状态字段（如果用户删掉了状态筛选，detailStatusTypes 为空数组，则查询所有状态）
            const statusFields = detailStatusTypes.length > 0
                ? detailStatusTypes.map((t) => {
                    const deliveryEfficiencyStatusMap: { [key: string]: string } = {
                        'on_time_delivery': 'isOnTimeDelivery',
                        'overdue_delivery': 'isOverdueDelivery',
                        'transferred_in': 'isTransferredIn',
                        'transferred_out': 'isTransferredOut',
                        'inDelivery': 'isInDelivery',
                        'damaged': 'isDamaged',
                        'allocatedParcels': 'isAllocatedParcels',
                        'pickedUpFromWarehouse': 'isPickedUpFromWarehouse',
                        'warehouseLost': 'isWarehouseLost',
                        'returned': 'isReturned',
                        'unclaimed': 'isUnclaimed',
                        'no_ascan_delivery': 'noAscanDelivery',
                        'delivered': 'isDeliveredLatest',
                        'in_progress': 'isInDelivery',
                        'lost': 'isFailedIncomplete'
                    };
                    return deliveryEfficiencyStatusMap[t];
                }).filter((v): v is string => typeof v === 'string' && v.length > 0)
                : undefined; // 如果 detailStatusTypes 为空，传递 undefined 查询所有状态

            // 如果有 statusFields，使用 fetchDetailsByStatusField 查询
            if (statusFields && statusFields.length > 0) {
                // Hub 页面只有区域维度：使用区域维度接口
                const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);
                
                await fetchDetailsByStatusField(
                    filters.taskDate.format('YYYY-MM-DD'),
                    statusFields,
                    pageRequest,
                    warehouseIdForQuery,
                    warehouseIdsForQuery,
                    hubIdForQuery,
                    selectedDspIds,
                    selectedAreaIds,
                    selectedDriverIds,
                    undefined
                );
            } else {
                const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);

                await fetchDetailsByStatusField(
                    filters.taskDate.format('YYYY-MM-DD'),
                    undefined,
                    pageRequest,
                    warehouseIdForQuery,
                    warehouseIdsForQuery,
                    hubIdForQuery,
                    selectedDspIds,
                    selectedAreaIds,
                    selectedDriverIds,
                    undefined
                );
            }
        } catch (error) {
            console.error('获取详情分页数据失败:', error);
        }
    };

    // 处理排序变化
    const handleSortChange = (sortBy: string, sortDirection: 'ASC' | 'DESC') => {
        // 只更新状态，让 useEffect 自动处理查询
        // 这样可以确保状态更新完成后再查询，并且与筛选条件正确结合
        setSortConfig({ sortBy, sortDirection });
        setCurrentPage(1); // 重置到第一页
        // 注意：不在这里直接调用 handleAreaFilter，让 useEffect 自动处理查询
    };

    // 处理详情排序变化
    const handleDetailSortChange = async (sortBy: string, sortDirection: 'ASC' | 'DESC') => {
        // 更新排序状态
        setDetailSortConfig({ sortBy, sortDirection });
        
        // 如果抽屉是打开的，重新获取数据
        if (drawerVisible && drawerData.length > 0 && lastDetailContext) {
            try {
        const pageRequest = {
                    page: 1, // 重置到第一页
            size: 10,
                    sortBy: sortBy,
                    sortDirection: sortDirection
                };

                // 使用保存的上下文数据
                const { type, record, filters: savedFilters } = lastDetailContext;

                // Hub 页面：传递 hubId（树状节点）
                const hubIdForSort = currentWarehouse?.siteType === SiteType.HUB
                    ? parseInt(currentWarehouse.id)
                    : undefined;
                const warehouseIdsForSort = (() => {
                    if (isWhsDimension() && detailFilters.selectedWarehouses?.length) {
                        const list = structuralWarehouses.map(whs => ({ id: String(whs.id), name: whs.name }));
                        const ids = (detailFilters.selectedWarehouses || []).map(name => {
                            const w = list.find(x => x.name === name);
                            return w?.id ? parseInt(w.id, 10) : null;
                        }).filter((id): id is number => id !== null && !isNaN(id));
                        return ids.length > 0 ? ids : undefined;
                    }
                    return undefined;
                })();
                const warehouseIdForSort = (warehouseIdsForSort?.length === 1 ? warehouseIdsForSort[0] : undefined) as string | number | undefined;
                
                // 根据当前详情类型重新获取数据 - 使用详情筛选条件
                if (isStatusType(type)) {
                    // 状态类型详情排序：使用详情筛选条件
                    const selectedDspIds = detailFilters.selectedDsps.length > 0
                        ? (() => {
                            const ids = detailFilters.selectedDsps.map(dspName => {
                                const dsp = dsps.find((d: any) => d.name === dspName);
                                return dsp ? dsp.id : null;
                            }).filter(id => id !== null) as number[];
                            // 如果转换后是空数组，返回 undefined
                            return ids.length > 0 ? ids : undefined;
                        })()
                        : undefined;

                    // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
                    const detailAreasForSort = areas;
                    const selectedAreaIds = detailFilters.selectedAreas.length > 0
                        ? (() => {
                            const ids = detailFilters.selectedAreas.map(areaName => {
                    const area = detailAreasForSort.find((a: { id: number; name: string }) => a.name === areaName);
                    return area ? area.id : null;
                }).filter((id): id is number => id !== null);
                            // 如果转换后是空数组，返回 undefined
                            return ids.length > 0 ? ids : undefined;
                        })()
                        : undefined;

                    const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);

                    const statusField = getStatusField(type, t);

                    if (statusField) {
                        // Hub 页面使用 'dsp' 维度（对应 Daily 页面的 'driver' 维度）
                        const isSavedDriverMode = savedFilters.viewMode === 'dsp';
                        if (isSavedDriverMode) {
                            // 司机维度：使用司机维度接口
                            // DSP/区域/司机下拉的选项统一来自主库远程搜索，此处只做名称→ID 换算
                            const detailAreasForSort2 = areas;
                            const selectedAreaIdsForSort = detailFilters.selectedAreas.length > 0
                                ? (() => {
                                    const ids = detailFilters.selectedAreas.map(areaName => {
                                        const area = detailAreasForSort2.find((a: { id: number; name: string }) => a.name === areaName);
                                        return area ? area.id : null;
                                    }).filter((id): id is number => id !== null);
                                    // 如果转换后是空数组，返回 undefined
                                    return ids.length > 0 ? ids : undefined;
                                })()
                                : undefined;
                            
                            await fetchDetailsByStatusFieldForDriver(
                                savedFilters.taskDate.format('YYYY-MM-DD'),
                                statusField ? [statusField] : undefined,
                                pageRequest,
                                warehouseIdForSort,
                                warehouseIdsForSort,
                                selectedDspIds,
                                selectedAreaIdsForSort,
                                selectedDriverIds,
                                undefined
                            );
                        } else {
                            const selectedDriverIdsForSort = toDriverIds(detailFilters.selectedDrivers);

                            await fetchDetailsByStatusField(
                                savedFilters.taskDate.format('YYYY-MM-DD'),
                                statusField ? [statusField] : undefined,
                                pageRequest,
                                warehouseIdForSort,
                                warehouseIdsForSort,
                                hubIdForSort,
                                selectedDspIds,
                                selectedAreaIds,
                                selectedDriverIdsForSort,
                                undefined
                            );
                        }
                    }
                } else if (currentDetailType === 'area' && 'areaId' in record) {
                    // 区域维度详情排序：使用详情筛选条件
                    const selectedDspIds = detailFilters.selectedDsps.length > 0
                        ? (() => {
                            const ids = detailFilters.selectedDsps.map(dspName => {
                                const dsp = dsps.find((d: any) => d.name === dspName);
                                return dsp ? dsp.id : null;
                            }).filter(id => id !== null) as number[];
                            // 如果转换后是空数组，返回 undefined
                            return ids.length > 0 ? ids : undefined;
                        })()
                        : undefined;

                    const selectedAreaIds = detailFilters.selectedAreas.length > 0
                        ? detailFilters.selectedAreas.map(areaName => {
                            const area = areas.find((a: { id: number; name: string }) => a.name === areaName);
                            return area ? area.id : null;
                        }).filter((id): id is number => id !== null)
                        : undefined;

                    // 如果没有 statusField，使用 fetchDetailsByStatusField查询（不传statusField）
                    const selectedDriverIds = toDriverIds(detailFilters.selectedDrivers);
                    
                    await fetchDetailsByStatusField(
                        savedFilters.taskDate.format('YYYY-MM-DD'),
                        undefined,
                        pageRequest,
                        warehouseIdForSort,
                        warehouseIdsForSort,
                        hubIdForSort,
                        selectedDspIds,
                        selectedAreaIds,
                        selectedDriverIds,
                        undefined
                    );
                }
            } catch (error) {
                console.error('详情排序失败:', error);
            }
        }
    };

    // 处理过滤器更新
    const handleFiltersUpdate = useCallback((newFilters: TaskMonitoringFilters) => {
        // 如果切换了维度，需要重置页码和清空数据
        const isViewModeChanged = newFilters.viewMode !== filters.viewMode;
        
        // 如果日期变化了，清空所有筛选条件
        const isTaskDateChanged = !newFilters.taskDate.isSame(filters.taskDate, 'day');
        
        // 如果切换了维度，清空两个维度的数据，确保重新加载
        if (isViewModeChanged) {
            // 切换维度时，清空两个维度的查询数据，确保下次查询是全新的
            clearAreaData();
            clearDriverData();
            // 切换维度时重置为默认排序：应派件量从高到低
            setSortConfig({ sortBy: 'allocatedParcels', sortDirection: 'DESC' });
        }
        
        // 如果日期变化了，清空筛选条件
        if (isTaskDateChanged) {
            newFilters = {
                ...newFilters,
                selectedWarehouses: [],
                selectedDsps: [],
                selectedAreas: [],
                selectedDrivers: [],
                selectedCities: []
            };
        }
        
        // 先重置页码，再更新筛选条件
        // 这样 useEffect 会在状态更新后自动触发查询，确保使用最新的筛选条件
        setCurrentPage(1); // 重置到第一页
        setFilters(newFilters);
        
        // 注意：不在这里直接调用 handleAreaFilter，让 useEffect 自动处理查询
        // 这样可以确保状态更新完成后再查询，避免使用旧的状态值
    }, [filters.viewMode, filters.taskDate, clearAreaData, clearDriverData]);

    // Hub 下子仓库列表（结构关联查询，供 WHS 下拉与详情抽屉使用）
    const filteredWarehouses = structuralWarehouses.map(whs => ({ id: String(whs.id), name: whs.name }));

    return {
        filters,
        setFilters: handleFiltersUpdate,
        driverData,
        areaData,
        // Hub 页面：无论展示 DSP/WHS 视角，summary 都来自区域汇总接口（getDailyTaskSummaryAreaList）
        summaryData: areaSummaryData,
        cities,
        warehouses: filteredWarehouses,
        dsps,
        areas,
        drivers,
        drawerDrivers,
        // 区域/司机下拉改为远程搜索，交给抽屉筛选组件驱动关键词
        onAreaSearch: setAreaKeyword,
        areaOptionsLoading,
        onDrawerDriverSearch: setDrawerDriverKeyword,
        drawerDriverOptionsLoading,
        drawerVisible,
        drawerTitle,
        drawerData,
        drawerColumns,
        setDrawerVisible,
        handleDetailClick,
        handleUndeliveredDistributionClick,
        handleDeliveryProgressClick,
        statusTypeOptions,
        handleDetailStatusTypeChange,
        dataUpdatedAtText,
        handlePageChange,
        handleDetailPageChange,
        handleSortChange,
        handleDetailSortChange,
        sortConfig,
        detailSortConfig,
        detailPagination,
        detailLoading,
        detailFilters,
        setDetailFilters,
        detailStatusTypes,
        loading: areaLoading,
        pagination: areaApiPagination ? {
            current: areaApiPagination.page,
            pageSize: areaApiPagination.size,
            total: areaApiPagination.totalElements,
        } : null,
        tablePagination: areaApiPagination ? {
            page: areaApiPagination.page,
            size: areaApiPagination.size,
            totalElements: areaApiPagination.totalElements,
            totalPages: areaApiPagination.totalPages,
            hasNext: areaApiPagination.hasNext,
            hasPrevious: areaApiPagination.hasPrevious,
        } : null,
        orderDetailVisible,
        orderId,
        setOrderDetailVisible,
        showOrderDetail,
        warehouseId: String(warehouseId),
        lastDetailContext,
        getCurrentSummaryDownloadArgs: () => currentSummaryDownloadArgsRef.current,
    };
};